/* @flow */
/* eslint-env es6,browser */

import * as u from './utils';
import adconfig from './adconfig';
import {openx} from './adserver/openx';
import {openx2} from './adserver/openx2';
import {openx2m} from './adserver/openx2m';

import bm from './bidmanager';

/**
 * @description Implements ad server functionality
 * @exports adserver
 * @requires {@link module:utils}
 * @requires {@link module:adserver/openx}
 * @requires {@link module:bidmanager}
 */
class _adserver {
  _doc: window.document;
  _win: window;
  _adservers: Array<adServerInterface>;
  _staticCustomVars: Object;
  _customVars: Object;
  constructor() {
    this._doc = document;
    this._win = window;
    this._adservers = [openx, openx2, openx2m];
    this._staticCustomVars = {};
    this._customVars = {};
  }

  /**
   * Initialize the object
   * @param {window} w - window object
   * @param {window.document} d - document object
   * @param {@link globalParams} g - global param object
   */
  init(w: window, d: window.document, g: globalParam) {
    this._doc = d;
    this._win = w;
    this._staticCustomVars = g.customVars;
    this._adservers.forEach(as => as.init(w, d, g));
  }

  /**
   * Update config - called adserver updateConfig
   * @param {Object.<string, perAdServerConfig>} o - object with config for each adserver from adconfig
   */
  updateConfig(o: adServerConfig) {
    this._adservers.forEach(as => as.updateConfig(o[as.getName()]));
  }

  /**
   * get custom variables
   * @access private
   */
  _getCustomVars() {
    var ret = this._staticCustomVars || {};
    if (u.isFunction(ret)) {
      //Let's pass page name to this function to get custom var
      ret = ret.call(this._win, adconfig.getPageName());
    }
    if (!u.isObject(ret)) {
      //data sanitization
      ret = {};
    }
    //merge user provided customVar
    u.extend(true, ret, this._customVars);
    return ret;
  }

  /**
   * load ads
   * @access private
   */
  _loadAds(posList: Array<string>, o: hbPartnerData) {
    posList = posList || [];
    let cumPosList = this._adservers.map(as => {
      let asName = as.getName();
      let asPosList = posList.filter(pos => adconfig.getPosSettings(pos).as === asName);
      if (asPosList.length) {
        as.loadAds(asPosList, o, this._getCustomVars());
      }
      return asPosList;
    }).reduce(((a, b) => a.concat(b)), []);
    let misPos = u.difference(posList, cumPosList).join(',');
    if (misPos.length) {
      u.logError(`adserver is not configured for ${misPos}`);
    }
  }

  /**
   * Bid and load ads
   * @param {string} pageName
   * @param {Array<string>} posListArr
   */
  loadAds(pageName: string, posListArr: Array<string>) {
    var activeBidPos = u.intersect(posListArr, bm.getBidPos(pageName)),
      inactiveBidPos = u.difference(posListArr, activeBidPos);
    u.logDebug(`active bid pos:${activeBidPos.join(',')} inactive bid pos:${inactiveBidPos.join(',')}`);

    //load inactive static ad positions immediately
    if (inactiveBidPos.length) {
      this._loadAds(inactiveBidPos, {});
    }

    //perform header bidding for remaining positions
    if (activeBidPos.length > 0) {
      bm.getBids(activeBidPos, pageName).then(o => this._loadAds(activeBidPos, o),
                                              () => this._loadAds(activeBidPos, {}));
    }
  }

  /**
   * set custom var
   * @access private
   */
  setCustomVar(k: string, v: Object) {
    k = k || '';
    if (k.length) {
      this._customVars[k] = v;
    }
  }
}

//export singleton object as default for this module
export default new _adserver();
